import { SceneManager } from './core/SceneManager';
import { GameLoop } from './core/GameLoop';
import { GameState } from './core/GameState';
import { EventBus } from './core/EventBus';
import { InputManager, type PickResult } from './core/InputManager';
import { PlatformRenderer } from './rendering/PlatformRenderer';
import { EnvironmentRenderer } from './rendering/EnvironmentRenderer';
import { AgentRenderer } from './rendering/AgentRenderer';
import { TaskRenderer } from './rendering/TaskRenderer';
import { PostProcessing } from './rendering/PostProcessing';
import { DispatchSystem } from './systems/DispatchSystem';
import { MonitorSystem } from './systems/MonitorSystem';
import { CorrectionSystem } from './systems/CorrectionSystem';
import { HUD } from './ui/HUD';
import { ValidationPanel } from './ui/ValidationPanel';
import { WorldLabels } from './ui/WorldLabels';
import { generatePuzzle, type PuzzleResult } from './puzzles/PuzzleGenerator';
import { FloatingText } from './ui/FloatingText';
import { MiniGames } from './ui/MiniGames';
import { AudioEngine } from './audio/AudioEngine';
import { SoundEffects } from './audio/SoundEffects';
import { AmbientDrone } from './audio/AmbientDrone';
import { LeaderboardService } from './services/LeaderboardService';
import { ShareService } from './services/ShareService';
import { TutorialSystem } from './systems/TutorialSystem';
import { VISUAL, ECONOMY, SCORING, RULES } from './constants';
import type { AgentState, Rank } from './types';
import { Group } from 'three';
import './ui/styles.css';

class Game {
  private sceneManager: SceneManager;
  private gameLoop: GameLoop;
  state: GameState;
  private events: EventBus;
  private platform: PlatformRenderer;
  private environment: EnvironmentRenderer;
  private postProcessing: PostProcessing;
  hud: HUD;
  private inputManager!: InputManager;
  dispatchSystem!: DispatchSystem;
  private monitorSystem!: MonitorSystem;
  correctionSystem!: CorrectionSystem;
  private worldLabels!: WorldLabels;
  private floatingText!: FloatingText;
  private miniGames!: MiniGames;
  private pendingBadge!: HTMLElement;
  private audioEngine: AudioEngine;
  private sfx: SoundEffects;
  private drone: AmbientDrone;
  private leaderboard: LeaderboardService;
  private tutorial: TutorialSystem;
  private validationPanel!: ValidationPanel;
  private cameraAngle = 0;
  private hudUpdateTimer = 0;

  // Dispatch interaction state
  private selectedTaskId: string | null = null;
  private hoveredObject: PickResult = null;

  // Validation state
  private pendingValidation: {
    agentId: string;
    puzzle: PuzzleResult;
  } | null = null;

  constructor() {
    const container = document.getElementById('app')!;

    // Core
    this.state = new GameState();
    this.events = new EventBus();

    // Scene setup
    this.sceneManager = new SceneManager(container);

    // Platform
    this.platform = new PlatformRenderer();
    this.sceneManager.platformGroup.add(this.platform.group);

    // Environment
    this.environment = new EnvironmentRenderer();
    this.sceneManager.environmentGroup.add(this.environment.nebulaMesh);
    this.sceneManager.environmentGroup.add(this.environment.particleMesh);

    // Post-processing
    this.postProcessing = new PostProcessing(
      this.sceneManager.renderer,
      this.sceneManager.scene,
      this.sceneManager.camera
    );

    window.addEventListener('resize', () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      this.postProcessing.setSize(w, h);
    });

    // Game systems
    this.dispatchSystem = new DispatchSystem(
      this.state,
      this.events,
      this.sceneManager.taskQueueGroup
    );

    this.monitorSystem = new MonitorSystem(this.state, this.events);
    this.correctionSystem = new CorrectionSystem(this.state, this.events);

    // Audio
    this.audioEngine = new AudioEngine();
    this.sfx = new SoundEffects(this.audioEngine);
    this.drone = new AmbientDrone(this.audioEngine);

    // Leaderboard
    this.leaderboard = new LeaderboardService();

    // Input manager
    this.inputManager = new InputManager(
      this.sceneManager.renderer.domElement,
      this.sceneManager.camera,
      this.sceneManager.agentsGroup,
      this.sceneManager.taskQueueGroup,
      (result) => this.handlePick(result),
      (result) => this.handleHover(result),
    );

    // HUD
    this.hud = new HUD(container, {
      onPlay: () => this.startGame(),
      onTutorial: () => this.startTutorial(),
      onRestart: () => this.restartGame(),
      onMenu: () => this.backToMenu(),
      onSpawn: () => this.spawnAgent(),
      onDespawn: () => this.despawnAgent(),
      onMute: () => this.audioEngine.toggleMute(),
      onGetLeaderboard: () => this.leaderboard.getTopScores(50),
      onSubmitScore: (name: string) => this.submitScore(name),
    });

    // World labels (floating labels over 3D objects)
    this.worldLabels = new WorldLabels(
      container,
      this.sceneManager.camera,
      this.sceneManager.renderer.domElement
    );

    // Floating text + pending badge
    this.floatingText = new FloatingText(container);
    this.pendingBadge = document.createElement('div');
    this.pendingBadge.className = 'pending-badge hidden';
    container.appendChild(this.pendingBadge);

    // Mini-games
    this.miniGames = new MiniGames(
      container,
      () => {
        // Aim booster reward: +1 combo
        this.state.combo++;
        this.floatingText.show('+1 COMBO (AIM BOOSTER)', '#4ecdc4');
      },
      () => {
        // Mental math reward: +1 life (capped at max)
        if (this.state.lives < RULES.maxLives) {
          this.state.lives++;
          this.hud.updateLives(this.state.lives);
          this.floatingText.show('+1 LIFE!', '#ff6b6b', undefined, undefined, 24);
        } else {
          // Already max lives — give score bonus instead
          this.state.score += 500;
          this.floatingText.show('+500 SCORE (MAX LIVES)', '#ffe66d');
        }
      },
      () => {}
    );

    // Validation panel
    this.validationPanel = new ValidationPanel(container, (approved) => {
      this.handleValidationDecision(approved);
    });

    // Tutorial (must be after HUD, so #hud exists)
    this.tutorial = new TutorialSystem(this.state, this.events, () => {
      this.restartGame();
    });

    // Event listeners
    this.setupEvents();

    // Keyboard shortcuts for spawn/despawn/mute
    window.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === 'Escape') {
        if (this.state.phase === 'playing') {
          this.showQuitConfirm();
        } else {
          this.backToMenu();
        }
        return;
      }
      if (this.state.phase !== 'playing') return;
      if (e.key === 's' || e.key === 'S') this.spawnAgent();
      else if (e.key === 'd' || e.key === 'D') this.despawnAgent();
      else if (e.key === 'q' || e.key === 'Q') {
        const muted = this.audioEngine.toggleMute();
        const muteBtn = document.getElementById('hud-mute');
        if (muteBtn) muteBtn.textContent = muted ? '[Q] SOUND OFF' : '[Q] SOUND ON';
      }
    });

    // Game loop
    this.gameLoop = new GameLoop(
      (dt, elapsed) => this.update(dt, elapsed),
      () => this.render()
    );

    this.gameLoop.start();

    // Portal: if ?portal=true, skip menu and start survival directly
    const params = new URLSearchParams(window.location.search);
    if (params.get('portal') === 'true') {
      this.startGame();
    } else {
      this.hud.showStartScreen();
    }
  }

  private setupEvents(): void {
    this.events.on('agent:crashed', ({ agentId }) => {
      this.sfx.crash();
      this.state.lives--;
      this.hud.updateLives(this.state.lives);
      this.removeAgent(agentId);
      if (this.state.lives <= 0) {
        this.gameOver('lives');
      }
    });

    this.events.on('agent:spawned', () => this.sfx.spawn());
    this.events.on('agent:despawned', () => this.sfx.despawn());
    this.events.on('agent:corrected', () => this.sfx.correct());
    this.events.on('task:assigned', () => this.sfx.click());
    this.events.on('task:expired', () => {
      this.sfx.expire();
      this.state.lives--;
      this.hud.updateLives(this.state.lives);
      this.floatingText.showPenalty('TASK EXPIRED — LIFE LOST!');
      if (this.state.lives <= 0) {
        this.gameOver('overflow');
      }
    });
    this.events.on('validation:decided', ({ correct }) => {
      if (correct) this.sfx.approve();
      else this.sfx.reject();
    });
    this.events.on('combo:milestone', ({ level }) => this.sfx.comboMilestone(level));
  }

  // ── Dispatch & Correction Interaction ───────────────
  private handlePick(result: PickResult): void {
    if (this.state.phase !== 'playing') return;

    if (!result) {
      this.clearSelection();
      return;
    }

    if (result.type === 'task') {
      // SELECT the task — highlight it and show connection lines to idle agents
      this.clearSelection();
      this.selectedTaskId = result.id;
      this.worldLabels.setSelectedTask(result.id);
      TaskRenderer.setSelected(result.object as Group, true);
      return;
    }

    if (result.type === 'agent') {
      const agent = this.state.agents.find(a => a.id === result.id);
      if (!agent) return;

      // Priority 1: Assign task if one is selected and agent is idle
      if (this.selectedTaskId && agent.status === 'idle') {
        const success = this.dispatchSystem.assignTaskToAgent(
          this.selectedTaskId,
          result.id
        );
        if (success) {
          this.clearSelection();
        }
        return;
      }

      // Priority 2: Open validation if agent is presenting
      if (agent.status === 'presenting' && agent.currentTask) {
        const puzzle = agent.currentTask.puzzleData as any;
        if (puzzle && puzzle.referenceLabel) {
          this.pendingValidation = { agentId: agent.id, puzzle };
          this.validationPanel.show(puzzle);
        }
        return;
      }

      // Priority 3: Correct drift
      if (agent.drift > 0.05) {
        this.correctionSystem.correctAgent(result.id);
      }
    }
  }

  private handleHover(result: PickResult): void {
    if (this.state.phase !== 'playing') return;

    if (this.hoveredObject && this.hoveredObject.type === 'task') {
      TaskRenderer.setHover(this.hoveredObject.object as Group, false);
    }

    this.hoveredObject = result;

    if (result && result.type === 'task') {
      TaskRenderer.setHover(result.object as Group, true);
    }

    // Update world labels hover state
    this.worldLabels.setHover(
      result?.type || null,
      result?.id || null
    );

    const canvas = this.sceneManager.renderer.domElement;
    canvas.style.cursor = result ? 'pointer' : 'default';
  }

  private clearSelection(): void {
    if (this.selectedTaskId) {
      const task = this.state.taskQueue.find(t => t.id === this.selectedTaskId);
      if (task && task.mesh) {
        const group = task.mesh.parent as Group;
        if (group) TaskRenderer.setSelected(group, false);
      }
      this.selectedTaskId = null;
      this.worldLabels.setSelectedTask(null);
    }
  }

  // ── Validation ──────────────────────────────────────
  private handleValidationDecision(approved: boolean): void {
    if (!this.pendingValidation) return;

    const { agentId, puzzle } = this.pendingValidation;
    const agent = this.state.agents.find(a => a.id === agentId);

    this.state.totalDecisions++;
    const hasError = puzzle.hasError;
    let correct = false;
    const isPriority = agent?.currentTask?.priority || false;

    if (approved && !hasError) {
      // Correct approve — good code shipped
      correct = true;
      const comboBonus = 1 + this.state.combo * SCORING.comboMultiplier;
      const computeReward = Math.floor(SCORING.baseTaskReward * (isPriority ? SCORING.priorityMultiplier : 1) * comboBonus);
      const scoreReward = Math.floor(SCORING.baseScoreReward * (isPriority ? SCORING.priorityMultiplier : 1) * comboBonus);
      this.state.compute += computeReward;
      this.state.score += scoreReward;
      this.state.combo++;
      this.floatingText.show(`+${computeReward} COMPUTE`, '#60a5fa');
      this.floatingText.show(`+${scoreReward} SCORE`, '#ffe66d', undefined, undefined, 14);
    } else if (!approved && hasError) {
      // Correct reject — caught bad code
      correct = true;
      const comboBonus = 1 + this.state.combo * SCORING.comboMultiplier;
      const scoreReward = Math.floor(SCORING.baseScoreReward * 1.5 * comboBonus);
      this.state.compute += SCORING.correctRejectionBonus;
      this.state.score += scoreReward;
      this.state.combo++;
      this.floatingText.show(`+${SCORING.correctRejectionBonus} COMPUTE`, '#60a5fa');
      this.floatingText.show(`+${scoreReward} SCORE (CAUGHT!)`, '#4ecdc4', undefined, undefined, 14);
    } else if (approved && hasError) {
      // Wrong approve — LOSE A LIFE
      this.state.combo = 0;
      this.state.lives--;
      this.hud.updateLives(this.state.lives);
      this.floatingText.showPenalty('💀 BAD CODE SHIPPED — LIFE LOST!');
      if (this.state.lives <= 0) {
        // Clean up agent before game over
        if (agent) { agent.currentTask = null; agent.taskProgress = 0; agent.status = 'idle'; }
        this.pendingValidation = null;
        this.gameOver('lives');
        return;
      }
    } else {
      // Wrong reject — LOSE A LIFE
      this.state.combo = 0;
      this.state.lives--;
      this.hud.updateLives(this.state.lives);
      this.floatingText.showPenalty('💀 FALSE REJECT — LIFE LOST!');
      if (this.state.lives <= 0) {
        if (agent) { agent.currentTask = null; agent.taskProgress = 0; agent.status = 'idle'; }
        this.pendingValidation = null;
        this.gameOver('lives');
        return;
      }
    }

    if (correct) this.state.correctDecisions++;
    this.state.updateTA();

    // Combo milestones — every 3 (more frequent = more exciting)
    if (this.state.combo > 0 && this.state.combo % 3 === 0) {
      this.floatingText.showCombo(this.state.combo);
      this.events.emit('combo:milestone', { level: this.state.combo });
    }

    this.events.emit('validation:decided', { approved, correct });

    // Clean up the agent
    if (agent) {
      agent.currentTask = null;
      agent.taskProgress = 0;
      agent.status = 'idle';
    }

    this.pendingValidation = null;
    // No auto-queue — player clicks the next presenting agent manually
  }

  // ── Game Flow ───────────────────────────────────────
  private startTutorial(): void {
    if (!this.audioEngine.isReady()) {
      this.audioEngine.init();
    }
    this.audioEngine.resume();

    this.state.reset();
    this.dispatchSystem.reset();
    this.state.compute = 100;
    this.state.phase = 'playing';
    this.gameLoop.resetTime();
    this.hud.showGameplay();
    this.hud.updateLives(this.state.lives);
    this.drone.start();

    // Spawn one agent for the tutorial
    this.spawnAgent();

    // Tutorial: prevent crashes, start guided flow
    this.monitorSystem.preventCrash = true;
    this.tutorial.start();
  }

  startGame(): void {
    // Initialize audio on first user gesture
    if (!this.audioEngine.isReady()) {
      this.audioEngine.init();
    }
    this.audioEngine.resume();

    this.tutorial.stop();
    this.monitorSystem.preventCrash = false;
    this.state.reset();
    this.dispatchSystem.reset();
    this.state.compute = 100;
    this.state.phase = 'playing';
    this.gameLoop.resetTime();
    this.hud.showGameplay();
    this.hud.updateLives(this.state.lives);

    // Start ambient drone
    this.drone.start();
  }

  private restartGame(): void {
    while (this.state.agents.length > 0) {
      this.removeAgent(this.state.agents[0].id);
    }
    this.dispatchSystem.reset();
    this.startGame();
  }

  private quitOverlay: HTMLElement | null = null;

  private showQuitConfirm(): void {
    if (this.quitOverlay) return; // already showing
    this.quitOverlay = document.createElement('div');
    this.quitOverlay.className = 'quit-confirm';
    this.quitOverlay.innerHTML = `
      <div class="quit-box glass-panel">
        <div class="quit-title">QUIT TO MENU?</div>
        <div class="quit-subtitle">Your run will be lost</div>
        <div class="quit-buttons">
          <button class="btn-play interactive" id="quit-yes">QUIT</button>
          <button class="btn-play interactive" id="quit-no">CONTINUE</button>
        </div>
      </div>
    `;
    document.getElementById('app')!.appendChild(this.quitOverlay);
    document.getElementById('quit-yes')!.addEventListener('click', () => {
      this.dismissQuitConfirm();
      this.backToMenu();
    });
    document.getElementById('quit-no')!.addEventListener('click', () => {
      this.dismissQuitConfirm();
    });
  }

  private dismissQuitConfirm(): void {
    if (this.quitOverlay) {
      this.quitOverlay.remove();
      this.quitOverlay = null;
    }
  }

  private backToMenu(): void {
    this.dismissQuitConfirm();
    this.tutorial.stop();
    this.monitorSystem.preventCrash = false;
    this.drone.stop();
    this.validationPanel.hide();
    this.pendingValidation = null;
    this.clearSelection();
    while (this.state.agents.length > 0) {
      this.removeAgent(this.state.agents[0].id);
    }
    this.dispatchSystem.reset();
    // Force-clear all 3D objects from scene groups
    while (this.sceneManager.agentsGroup.children.length > 0) {
      this.sceneManager.agentsGroup.remove(this.sceneManager.agentsGroup.children[0]);
    }
    while (this.sceneManager.taskQueueGroup.children.length > 0) {
      this.sceneManager.taskQueueGroup.remove(this.sceneManager.taskQueueGroup.children[0]);
    }
    this.worldLabels.clearAll();
    this.pendingBadge.classList.add('hidden');
    this.state.reset();
    this.state.phase = 'start';
    this.hud.showStartScreen();
  }

  private async submitScore(playerName: string): Promise<void> {
    const rank = this.calculateRank(this.state.score);
    await this.leaderboard.submitScore({
      playerName,
      score: this.state.score,
      apm: this.state.kpis.apm,
      survivalTime: this.state.time,
      peakAgents: this.state.peakAgents,
      rank,
      dr: this.state.kpis.dr,
      vs: this.state.kpis.vs,
      ce: this.state.kpis.ce,
      ta: this.state.kpis.ta,
    });
  }

  private gameOver(reason: 'lives' | 'overflow'): void {
    this.state.phase = 'gameover';
    this.state.score += Math.floor(this.state.time * this.state.peakAgents * 2);
    const rank = this.calculateRank(this.state.score);
    this.drone.stop();
    this.validationPanel.hide();
    this.pendingValidation = null;
    this.hud.showGameOver(
      this.state.score,
      this.state.kpis.apm,
      this.state.time,
      this.state.peakAgents,
      rank
    );
    this.events.emit('game:over', { reason });
  }

  // ── Agent Management ────────────────────────────────
  spawnAgent(): void {
    if (this.state.phase !== 'playing') return;
    if (!this.state.canSpawnAgent()) return;

    const cost = this.state.getNextSpawnCost();
    this.state.compute -= cost;

    const id = `agent_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const slotIndex = this.findAvailableSlot();

    const agent: AgentState = {
      id,
      status: 'idle',
      drift: 0,
      driftSpeed: this.state.difficulty.agentDriftSpeed,
      currentTask: null,
      taskProgress: 0,
      correctionCooldown: 0,
      spawnCost: cost,
      slotIndex,
      mesh: null,
    };

    const mesh = AgentRenderer.createAgentMesh(agent);
    agent.mesh = mesh;
    this.sceneManager.agentsGroup.add(mesh);

    this.state.agents.push(agent);
    this.state.peakAgents = Math.max(this.state.peakAgents, this.state.agents.length);
    this.state.totalAgentsSpawned++;

    this.events.emit('agent:spawned', { agentId: id });
  }

  private despawnAgent(): void {
    if (this.state.phase !== 'playing') return;
    if (this.state.agents.length === 0) return;

    const agent = this.state.agents[this.state.agents.length - 1];
    const refund = Math.floor(agent.spawnCost * ECONOMY.despawnRefundRate);
    this.state.compute += refund;

    this.removeAgent(agent.id);
    this.events.emit('agent:despawned', { agentId: agent.id });
  }

  private removeAgent(agentId: string): void {
    const idx = this.state.agents.findIndex(a => a.id === agentId);
    if (idx === -1) return;

    const agent = this.state.agents[idx];
    if (agent.mesh) {
      this.sceneManager.agentsGroup.remove(agent.mesh);
      agent.mesh.traverse(child => {
        if ('geometry' in child && child.geometry) {
          (child as any).geometry.dispose();
        }
        if ('material' in child && child.material) {
          const mat = child.material;
          if (Array.isArray(mat)) mat.forEach(m => m.dispose());
          else (mat as any).dispose();
        }
      });
    }
    this.state.agents.splice(idx, 1);
  }

  private findAvailableSlot(): number {
    const used = new Set(this.state.agents.map(a => a.slotIndex));
    for (let i = 0; i < 10; i++) {
      if (!used.has(i)) return i;
    }
    return 0;
  }

  private calculateRank(score: number): Rank {
    if (score >= 50000) return 'S+';
    if (score >= 30000) return 'S';
    if (score >= 15000) return 'A';
    if (score >= 7000) return 'B';
    if (score >= 3000) return 'C';
    return 'D';
  }

  // ── Update Loop ─────────────────────────────────────
  update(dt: number, elapsed: number): void {
    this.platform.update(elapsed);
    this.environment.update(elapsed);

    if (this.state.phase === 'playing') {
      this.state.time += dt;

      // Tutorial: apply difficulty overrides
      if (this.tutorial.active) {
        this.tutorial.update(dt);
        const overrides = this.tutorial.getDifficultyOverrides();
        if (overrides) {
          if (overrides.taskSpawnInterval !== undefined) this.state.difficulty.taskSpawnInterval = overrides.taskSpawnInterval;
          if (overrides.agentDriftSpeed !== undefined) {
            this.state.difficulty.agentDriftSpeed = overrides.agentDriftSpeed;
            // Also update existing agents' drift speed
            for (const agent of this.state.agents) {
              agent.driftSpeed = overrides.agentDriftSpeed;
            }
          }
          if (overrides.taskTimeout !== undefined) this.state.difficulty.taskTimeout = overrides.taskTimeout;
          if (overrides.correctionCooldown !== undefined) this.state.difficulty.correctionCooldown = overrides.correctionCooldown;
        }
      }

      // Systems
      this.dispatchSystem.update(dt, elapsed);
      this.monitorSystem.update(dt);
      this.correctionSystem.update(dt);

      // Update agent visuals + task progress
      for (const agent of this.state.agents) {
        if (agent.status === 'working' && agent.currentTask) {
          const baseDuration = 2 + agent.currentTask.complexity * 0.4;
          const taskDuration = baseDuration / this.tutorial.getTaskSpeedMultiplier();
          agent.taskProgress += dt / taskDuration;

          if (agent.taskProgress >= 1) {
            agent.taskProgress = 1;

            // Generate puzzle for validation
            const puzzle = generatePuzzle(
              agent.currentTask.type,
              agent.currentTask.complexity,
              this.state.difficulty.errorSubtlety
            );
            agent.currentTask.puzzleData = puzzle;

            // Tutorial: auto-approve during non-validate steps
            if (this.tutorial.shouldAutoApprove()) {
              const reward = SCORING.baseTaskReward * (agent.currentTask.priority ? SCORING.priorityMultiplier : 1);
              this.state.compute += reward;
              this.state.score += SCORING.baseScoreReward;
              this.state.combo++;
              agent.currentTask = null;
              agent.taskProgress = 0;
              agent.status = 'idle';
            } else {
              // Agent waits in 'presenting' — player must CLICK to validate
              agent.status = 'presenting';
            }
          }
        }

        AgentRenderer.updateAgentVisuals(agent, elapsed);
      }

      // HUD update (throttled)
      this.hudUpdateTimer += dt;
      if (this.hudUpdateTimer > 0.066) {
        this.hudUpdateTimer = 0;
        this.state.updateDR();
        this.state.updateVS();
        this.state.updateCE();
        this.state.updateTA();
        this.state.updateAPM();
        this.hud.update(this.state);
        this.worldLabels.update(this.state);

        // Pending validations badge
        const pendingCount = this.state.agents.filter(a => a.status === 'presenting').length;
        if (pendingCount > 0) {
          this.pendingBadge.classList.remove('hidden');
          this.pendingBadge.textContent = `PENDING VALIDATIONS: ${pendingCount}`;
        } else {
          this.pendingBadge.classList.add('hidden');
        }

        // Update ambient drone tension
        const maxDrift = this.state.agents.reduce((max, a) => Math.max(max, a.drift), 0);
        const queuePressure = this.state.taskQueue.length / Math.max(1, this.state.difficulty.queueMaxSize);
        const tension = Math.min(1, maxDrift * 0.6 + queuePressure * 0.4);
        this.drone.setTension(tension);
      }
    }

    // Camera auto-orbit
    this.cameraAngle += VISUAL.autoOrbitSpeed * dt;
    const cam = this.sceneManager.camera;
    cam.position.x = Math.sin(this.cameraAngle) * VISUAL.cameraDistance;
    cam.position.z = Math.cos(this.cameraAngle) * VISUAL.cameraDistance;
    cam.position.y = VISUAL.cameraHeight;
    cam.lookAt(0, 0, 0);
  }

  render(): void {
    this.postProcessing.render();
  }
}

// Boot
(window as any).__game = new Game();
